import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { type Chatbox, type Session } from 'talkjs/all';
import { RCS_CONVERSATION_STATUS_ACTIVE } from '@rsos/constants/rcsStatuses';
import useTF911LoggedInUser from '@rsos/hooks/talkjs/useTF911LoggedInUser';
import { useOpenChatTypes } from '@rsos/reduxDataStore/src/features/chats/hooks';
import { CHAT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { type PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import { useLazyIndicateTypingRcsConversationQuery } from '@rsos/reduxDataStore/src/features/rcs/rcsApi';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import {
  addParticipant,
  setRcsUnseenMessageStatus,
  setRcsunblurMessageUrls,
} from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { buildDoNotTranslateSendOptions } from '@rsos/utils/SmartPhrases';
import { createTalkSession, createTalkUser } from '@rsos/utils/talkJS';
import { RootState } from '../../../../app';
import useTalkJSTheme from '../../../../hooks/useTalkJSTheme';
import SuggestedResponses from '../../../PreconfiguredMessages/SuggestedResponses';
import RequestLocation from '../../../RequestLocation';
import { useChatContext } from '../ChatContext';
import { useDoNotTranslateMessageField } from '../useDoNotTranslateMessageField';
import { useRequestLocation } from '../useRequestLocation';
import { useTrackSentLocationRequests } from '../useTrackSentLocationRequests';
import styles from './RcsChat.module.scss';
import {
  trackSendPreconfiguredResponse,
  trackViewAllPreconfiguredResponses,
} from './RcsChatBoxTrackings';
import { RCS_TYPING_INDICATOR_WINDOW } from './constants';

interface RcsChatProps {
  callerID: string;
  isRcsChatBoxVisible: boolean;
  preconfiguredMessages: PreconfiguredResponse[] | undefined;
  hasRequestLocationAccess: boolean;
}

const RcsChat = ({
  callerID,
  isRcsChatBoxVisible,
  preconfiguredMessages,
  hasRequestLocationAccess,
}: RcsChatProps) => {
  const dispatch = useDispatch();
  const {
    setIsAllMessagesVisible,
    selectedPreconfiguredMessage,
    setSelectedPreconfiguredMessage,
    locationRequestPathway,
  } = useChatContext();
  const chatRef = useRef<HTMLDivElement>(null);

  const [chatSession, setChatSession] = useState<Session | null>(null);
  const [chatbox, setChatbox] = useState<Chatbox | null>(null);

  const { protectPrefilledPhrases } = useDoNotTranslateMessageField(chatbox);

  const selectedTab = useSelector(
    (state: RootState) => state.eventPane.selectedTab,
  );
  const isRcsChatVisibleRedux = useOpenChatTypes().rcs;

  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const lastTriggeredRef = useRef<number | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const chatTheme = useTalkJSTheme('rcs');
  const rcsConversation = useSelectedRcsConversation(callerID);

  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application || '',
      )?.name,
  );

  const unblurMessageUrls = useSelector(
    (state: RootState) => state.rcs.unblurMessageUrls,
  );

  const [triggerIndicateTypingRcsConversation] =
    useLazyIndicateTypingRcsConversationQuery();

  const rcsConversationId = useMemo(() => {
    if (!rcsConversation || !rcsConversation.data) return;
    return rcsConversation.data.conversation_id;
  }, [rcsConversation]);

  const loggedInUser = useTF911LoggedInUser();

  const shouldDisplayMessageField = useMemo(() => {
    return (
      rcsConversation?.data?.conversation_status ===
      RCS_CONVERSATION_STATUS_ACTIVE
    );
  }, [rcsConversation]);

  const triggerTypingIndicator = useCallback(() => {
    const now = Date.now();
    if (now - (lastTriggeredRef.current ?? 0) >= RCS_TYPING_INDICATOR_WINDOW) {
      lastTriggeredRef.current = now;
      triggerIndicateTypingRcsConversation({ callerID, orgName });
    }
  }, [callerID, orgName, triggerIndicateTypingRcsConversation]);

  const handleTyping = useCallback(() => {
    if (!hasStartedTyping) {
      setHasStartedTyping(true);
      triggerTypingIndicator();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setHasStartedTyping(false);
      typingTimeoutRef.current = null;
    }, RCS_TYPING_INDICATOR_WINDOW);
  }, [triggerTypingIndicator, hasStartedTyping]);

  // Mount the TalkJS session and chat container
  useEffect(() => {
    let session: Session | undefined, chat: Chatbox | undefined;
    try {
      (async () => {
        session = await createTalkSession(loggedInUser);

        chat = session?.createChatbox({
          theme: {
            name: chatTheme,
            custom: {
              app: 'rcs',
            },
          },
          showChatHeader: false,
          messageField: {
            visible: false,
            placeholder: 'Type Message',
          },
          showTranslationToggle: false,
          presence: {
            visible: false,
          },
          captureKeyboardEvents: true,
        });

        if (chatRef.current && chat) {
          chat.mount(chatRef.current);
        }

        dispatch(
          addParticipant({
            callerID,
            participant: loggedInUser,
          }),
        );

        setChatSession(session || null);
        setChatbox(chat || null);
      })();
    } catch (error) {
      if (session) {
        setChatbox(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTheme, loggedInUser, dispatch]);

  useEffect(() => {
    if (chatbox && chatSession && rcsConversationId) {
      const conversation =
        chatSession.getOrCreateConversation(rcsConversationId);
      conversation.setParticipant(createTalkUser(loggedInUser));

      conversation.setAttributes({
        custom: { unblurMessageUrls: JSON.stringify(unblurMessageUrls) },
      });

      chatbox.select(conversation);
    }
  }, [
    chatSession,
    chatbox,
    rcsConversationId,
    loggedInUser,
    unblurMessageUrls,
  ]);

  useEffect(() => {
    if (chatbox && rcsConversationId) {
      chatbox.messageField.setVisible(shouldDisplayMessageField);

      chatbox.onBlur(() => {
        chatbox.setPresence({ visible: false });
      });

      chatbox.onFocus(() => {
        chatbox.setPresence({ visible: true });
      });

      chatbox.onKeydown(e => {
        if (e.key !== 'Enter') {
          handleTyping();
        }
      });

      chatbox.onCustomMessageAction('toggleBlur', event => {
        const attachment = event?.message?.attachment;
        if (!attachment) return;

        if (unblurMessageUrls.includes(attachment.url)) {
          dispatch(
            setRcsunblurMessageUrls({
              unblurMessageUrls: unblurMessageUrls.filter(
                (url: string) => url !== attachment.url,
              ),
            }),
          );
        } else {
          dispatch(
            setRcsunblurMessageUrls({
              unblurMessageUrls: [...unblurMessageUrls, attachment.url],
            }),
          );
        }
      });
    }
  }, [
    chatSession,
    chatbox,
    rcsConversationId,
    loggedInUser,
    shouldDisplayMessageField,
    handleTyping,
    unblurMessageUrls,
    dispatch,
  ]);

  const rcsUnreadMessages = useSelector(
    (state: RootState) => state.rcs?.unreadMessages || {},
  );
  const hasUnreadMessage =
    rcsUnreadMessages[callerID] || rcsConversation?.ui?.unseenMessage;

  useEffect(() => {
    const chatVisible =
      isRcsChatVisibleRedux !== undefined
        ? isRcsChatVisibleRedux
        : isRcsChatBoxVisible;

    if (
      selectedTab === CHAT_TAB &&
      chatVisible &&
      rcsConversationId &&
      chatSession &&
      chatbox &&
      isRcsChatBoxVisible &&
      hasUnreadMessage
    ) {
      dispatch(setRcsUnseenMessageStatus({ callerID, unseenMessage: false }));
    }
  }, [
    selectedTab,
    isRcsChatVisibleRedux,
    rcsConversationId,
    chatSession,
    chatbox,
    isRcsChatBoxVisible,
    hasUnreadMessage,
    rcsUnreadMessages,
    rcsConversation,
    callerID,
    dispatch,
  ]);

  useEffect(() => {
    if (!chatbox || !shouldDisplayMessageField) return;

    if (selectedPreconfiguredMessage) {
      if (selectedPreconfiguredMessage.isSending) {
        const conversation = rcsConversationId
          ? chatSession?.getOrCreateConversation(rcsConversationId)
          : null;
        conversation?.sendMessage(
          selectedPreconfiguredMessage.message.body,
          buildDoNotTranslateSendOptions(
            selectedPreconfiguredMessage.message.resolvedPhrases,
          ),
        );
        chatbox.messageField.setText('');
      } else {
        chatbox.messageField.setText(selectedPreconfiguredMessage.message.body);
        protectPrefilledPhrases(
          selectedPreconfiguredMessage.message.resolvedPhrases,
        );
      }
      setSelectedPreconfiguredMessage(null);
    }
  }, [
    chatbox,
    chatSession,
    protectPrefilledPhrases,
    rcsConversationId,
    selectedPreconfiguredMessage,
    shouldDisplayMessageField,
    setSelectedPreconfiguredMessage,
  ]);

  // Unsubscribed on cleanup: createTalkSession returns a process-wide
  // singleton, so a handler left behind when preconfiguredMessages refetches
  // would report every later send a second time.
  useEffect(() => {
    if (!chatSession || !rcsConversationId) return;

    const subscription = chatSession.onMessage(message => {
      const matchedMessage = preconfiguredMessages?.find(
        msg => msg.body === message.body,
      );
      if (matchedMessage) {
        trackSendPreconfiguredResponse(matchedMessage, rcsConversationId);
      }
    });

    return () => subscription.unsubscribe();
  }, [chatSession, rcsConversationId, preconfiguredMessages]);

  useTrackSentLocationRequests({
    chatSession,
    pathwayType: locationRequestPathway,
    callerID,
    conversationId: rcsConversationId ?? undefined,
  });

  const handleViewClick = () => {
    trackViewAllPreconfiguredResponses(rcsConversationId ?? '');
    setIsAllMessagesVisible(true);
  };

  const { handleRequestLocation, locationError } = useRequestLocation({
    chat: chatbox,
    isConversationEnded: !shouldDisplayMessageField,
    preconfiguredMessages,
    pathwayType: locationRequestPathway,
    conversationId: rcsConversationId ?? undefined,
    protectPhrases: protectPrefilledPhrases,
  });

  const hasSuggestedResponses =
    shouldDisplayMessageField && isRcsChatBoxVisible;

  return (
    <div
      className={`${styles.chatContainer} ${isRcsChatBoxVisible && rcsConversationId ? styles.withBorder : ''}`}
    >
      <div ref={chatRef} className={styles.chatMountPoint} />
      {hasSuggestedResponses && (
        <>
          <div className={styles.suggestedResponsesRow}>
            {hasRequestLocationAccess && (
              <RequestLocation onClick={handleRequestLocation} />
            )}
            <SuggestedResponses handleViewClick={handleViewClick} />
          </div>
          {locationError && (
            <p className={styles.locationError}>
              Something went wrong, please try again
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default RcsChat;
